<script lang="ts" setup>
  import { BaseButton, Drawer, PressReleaseDrawer, Text, ShareButton } from '@bt/design-system'
  import { storeToRefs } from 'pinia'
  import { head } from 'lodash-es'
  import { useFetchPressReleases } from '@/composables/useFetchPressReleases'
  import { useFetchPress, useFetchPaginationPress } from '@/composables/useFetchPress'
  import { getDate } from '@/utils/getDate'
  import updateRoute from '@/utils/updateRoute'
  import { useAppStore } from '@/stores'

  import renderRichText from '@/utils/renderSbRichText'

  interface IBanner {
    title: string
    text: string
    day: string
    monthYear: string
    slug: string
  }

  interface ITermsAndConditionsContent {
    _editable: string
    _uid: string
    acceptText: string
    component: string
    content: Object
    declineText: string
    title: string
  }

  interface ITermsAndConditions {
    content: ITermsAndConditionsContent
  }

  interface ISpecialRelease {
    termsAndConditions: ITermsAndConditions
    termsAndConditionsDecline: ITermsAndConditions
  }

  defineProps<{
    blok: any
  }>()

  const appStore = useAppStore()
  const config = useRuntimeConfig()
  const pagesPerEnv = () => {
    return config.public.env === 'production' ? 4 : 90
  }
  const { locale, t } = useI18n()
  const { query } = useRoute()
  const pressPage = ref(1)
  const pressPerPage = ref(pagesPerEnv())
  const pressYear = ref(1998)
  const banners: IBanner[] = ref([])
  const isDrawerVisible = ref(false)
  const activeEvent = ref({ originalDate: '', date: '', title: '', text: '', slug: '' })
  const eventData = ref({})
  const termsAndConditions = ref({})
  const termsAndConditionsDecline = ref({})
  const totalItems = ref(0)
  const showLoader = ref(false)
  const isPreviousBtnDisabled = ref(false)
  const isNextBtnDisabled = ref(false)
  const isSpecRelease = ref(false)
  const tAndCDeclined = ref(false)

  // DROPDOWN BaseSelect
  const currentYear = new Date().getFullYear()
  const currentYearItem = { title: currentYear, key: currentYear }
  const selectedYear = ref(currentYearItem)

  const { fetchPressReleases } = useFetchPressReleases()
  const { fetchPress } = useFetchPress()
  const { fetchPaginationPress } = useFetchPaginationPress()
  const { cmsPath, loading } = storeToRefs(useAppStore())

  const getPressYears = () => {
    const yearsArray = []
    while (pressYear.value <= currentYear) {
      const newPressYear = pressYear.value++
      yearsArray.push({
        title: newPressYear,
        key: newPressYear
      })
    }

    return yearsArray.reverse()
  }

  const years = getPressYears()

  onMounted(() => {
    if (query?.press) {
      openEvent(query.press)
    }
    appStore.setAvailableLocales(['en', 'lv', 'lt', 'et', 'fi', 'de', 'ru'])
  })

  const changePressYear = (item: any) => {
    selectedYear.value = item
    pressPage.value = 1
    banners.value = []
    refresh().then(() => {
      banners.value = data.value?.items
      totalItems.value = data.value?.total
    })
  }

  const { data, error, refresh } = await useAsyncData(
    'press',
    () => fetchPressReleases(locale.value, pressPerPage.value, pressPage.value, selectedYear.value.key),
    {
      watch: [selectedYear.value],
      transform: (response) => {
        response.items.forEach((resp: any) => {
          resp.monthYear = t(`reusable.monthShort.${resp.month}`) + ' ' + resp.year
        })
        return response
      }
    }
  )

  if (data.value) {
    banners.value = data.value.items
    totalItems.value = data.value?.total
  }

  if (error.value) {
    throw createError({ statusCode: 404, statusMessage: 'Page Not Found' })
  }

  const getPress = async (slug: string) => {
    await fetchPress(locale.value, slug).then((items) => {
      if (!items.length) {
        throw showError({ statusCode: 404, statusMessage: 'Page Not Found' })
      }

      singlePressFetchCallback(items)
    })
    await getPaginationPress(activeEvent.value.originalDate, true, true)
    await getPaginationPress(activeEvent.value.originalDate, false, true)
  }

  const getPaginationPress = async (date: string, isFutureEvent: boolean, skipDataAssigment?: boolean) => {
    await fetchPaginationPress(locale.value, date, isFutureEvent).then((re) =>
      skipDataAssigment ? checkIfStepButtonIsAvailable(re, isFutureEvent) : singlePressFetchCallback(re, isFutureEvent)
    )
  }

  const checkIfStepButtonIsAvailable = (re: any, isFutureEvent: boolean) => {
    const items = re.filter((item: any) => item.content.publishDate)

    if (items.length < 1 && typeof isFutureEvent === 'boolean') {
      if (isFutureEvent) {
        isNextBtnDisabled.value = true
      } else {
        isPreviousBtnDisabled.value = true
      }
    }
  }

  const singlePressFetchCallback = (re: any, isFutureEvent?: boolean) => {
    const areItemsValid = re.every((item: any) => item.content.publishDate)

    if ((re.length < 2 || !areItemsValid) && typeof isFutureEvent === 'boolean') {
      if (isFutureEvent) {
        isNextBtnDisabled.value = true
      } else {
        isPreviousBtnDisabled.value = true
      }
    } else {
      isNextBtnDisabled.value = false
      isPreviousBtnDisabled.value = false
    }

    const data = head(re)
    if (!data?.content?.publishDate) {
      loading.value = false

      return
    }

    const text = computed(() => {
      return renderRichText(data.content.lead)
    })

    const slug =
      locale.value === 'en'
        ? data.full_slug.replace('press-releases/', '')
        : data.full_slug.replace(`${locale.value}/press-releases/`, '')

    let eventDate = ''

    if (data?.content?.publishDate) {
      const getDateData = getDate(data.content.publishDate)

      if (getDateData) {
        const { day, month, year } = getDateData
        eventDate = `${day} ${t(`reusable.monthShort.${month}`)} ${year}`
      }
    }

    activeEvent.value = {
      originalDate: data.content.publishDate,
      date: eventDate,
      title: data.content.title,
      text,
      slug
    }

    eventData.value = data.content.body

    if (data.content.specRelease) {
      setSpecialReleaseData(data.content)
    } else {
      setRegularReleaseData()
    }

    updateRoute('press', slug)
  }

  const setRegularReleaseData = () => {
    isDrawerVisible.value = true
    isSpecRelease.value = false
    termsAndConditions.value = {}
    termsAndConditionsDecline.value = {}
  }

  const setSpecialReleaseData = (data: ISpecialRelease) => {
    isDrawerVisible.value = false
    isSpecRelease.value = true
    termsAndConditions.value = data.termsAndConditions
    termsAndConditionsDecline.value = data.termsAndConditionsDecline
  }

  const openEvent = async (slug: string) => {
    await getPress(slug)
  }

  const closeEvent = () => {
    isDrawerVisible.value = false
    updateRoute()
  }

  const loadMore = () => {
    pressPage.value++
    refresh()
      .then(() => {
        data.value.items.forEach((bannerData: IBanner) => {
          banners.value.push(bannerData)
        })
      })
      .catch((e) => {
        throw createError({ statusCode: 404, statusMessage: e })
      })
  }

  const loadEvent = (isFutureEvent: boolean) => {
    showLoader.value = true

    if (activeEvent.value.originalDate) {
      getPaginationPress(activeEvent.value.originalDate, isFutureEvent, false)
    }

    document.querySelector('.drawer-container')?.scrollTo(0, 0)
  }

  const fullPressEventUrl = computed(
    () => `${config.public.baseUrl}/${locale.value}/${cmsPath.value}?press=${activeEvent.value.slug}`
  )

  const acceptSpecReleaseTerms = () => {
    isSpecRelease.value = false
    isDrawerVisible.value = true
  }

  const declineSpecialReleaseTerms = () => {
    isSpecRelease.value = false
    tAndCDeclined.value = true
  }

  const handleTACClose = () => {
    isSpecRelease.value = false
    tAndCDeclined.value = false
    closeEvent()
  }
</script>

<template>
  <div class="press-banner__wrapper">
    <div class="year-dropdown-wrapper">
      <span class="dropdown-title">{{ $t('press-releases.select-year') }}</span>
      <div class="dropdown">
        <BaseSelect
          :active-item="selectedYear"
          :items="years"
          :placeholder="$t('press-releases.select-year')"
          :mobile-label="$t('press-releases.select-year')"
          @on-change="changePressYear"
        />
      </div>
    </div>

    <template v-if="banners?.length">
      <PressBanner
        v-for="banner in banners"
        :key="banner.slug"
        :blok="banner"
        @open-event="openEvent(banner.slug)"
      />

      <BaseButton
        v-if="pressPage * pressPerPage < totalItems"
        :is-block="true"
        block-type="full"
        color="ghost"
        :title="$t('press-releases.load-more')"
        @click="loadMore"
      />
    </template>

    <template v-else-if="!loading">
      <StoryblokComponent
        v-for="block in blok.body"
        :key="block._uid"
        :blok="block"
      />
    </template>

    <LazyGeneralModal
      :model-value="isSpecRelease || tAndCDeclined"
      :full-screen="isSpecRelease"
      @update:model-value="(val: boolean) => !val && handleTACClose()"
    >
      <LazyGeneralTermsAndConditions
        v-if="isSpecRelease"
        :data="termsAndConditions.content"
        :full-screen="isSpecRelease"
        @accept="acceptSpecReleaseTerms"
        @decline="declineSpecialReleaseTerms"
      />

      <LazyGeneralTermsAndConditions
        v-if="tAndCDeclined"
        :data="termsAndConditionsDecline.content"
        :full-screen="false"
        @accept="handleTACClose"
      />
    </LazyGeneralModal>

    <Drawer
      v-model="isDrawerVisible"
      :rounded="true"
      backdrop-color="light"
      side="right"
      @update:model-value="closeEvent"
    >
      <PressReleaseDrawer
        :data="activeEvent"
        @close-drawer="closeEvent"
      >
        <template #content>
          <div
            :key="activeEvent.slug"
            class="press-banner__content"
          >
            <Text
              :text="activeEvent.title"
              type="h2"
            />
            <Text
              :text="activeEvent.text"
              type="lead"
              class="mb-4"
            />
            <template
              v-for="block in eventData"
              :key="block._uid"
            >
              <StoryblokComponent :blok="block" />
            </template>
          </div>
        </template>
        <template #share>
          <div class="flex items-center gap-4">
            <Text
              :text="`${$t('press-releases.share-event')}:`"
              type="subtitle1"
              class="mr-2"
            />
            <ShareButton
              :item="{ key: 'twitter', url: fullPressEventUrl, text: encodeURIComponent(activeEvent.title) }"
            />
            <ShareButton :item="{ key: 'facebook', url: fullPressEventUrl }" />
            <ShareButton
              :item="{ key: 'linkedin', url: fullPressEventUrl, text: encodeURIComponent(activeEvent.title) }"
            />
          </div>
        </template>
        <template #actions="{ className }">
          <BaseButton
            :class="className"
            color="ghost"
            :title="$t('press-releases.load-previous')"
            :disabled="isPreviousBtnDisabled"
            @click="loadEvent(false)"
          />
          <BaseButton
            :class="className"
            color="ghost"
            :title="$t('press-releases.load-next')"
            :disabled="isNextBtnDisabled"
            @click="loadEvent(true)"
          />
        </template>
      </PressReleaseDrawer>
    </Drawer>
  </div>
</template>

<style lang="scss" scoped>
  @import '@/assets/styles/common.scss';

  .press-banner {
    &__wrapper {
      min-height: 500px;
      margin-bottom: rem(32);
      padding: 0 rem(16);
    }

    &__content {
      padding: 0 rem(16);

      @include tablet-up {
        padding: 0 rem(48);
      }
    }
  }

  :deep {
    .grow {
      h1,
      h2,
      h3,
      h4 {
        margin: rem(16) 0;
      }
    }
  }
</style>
